import { useEffect, useState } from "react";

import {
  fetchConversationViewCapabilities,
  type ConversationViewCapabilities,
} from "@/api/conversation-views";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import type { AccessTokenGetter } from "@/lib/utils";

/**
 * Probe additive conversation-view contracts once per active session. Missing
 * or older-server responses are ordinary read-only compatibility states.
 */
export function useConversationViewCapabilities(
  sessionId: string | null,
  getToken: AccessTokenGetter,
  bootstrapCapabilities?: { browser_file_pins: number; pinned_file_editing: number } | null,
  bootstrapPending = false,
): ConversationViewCapabilities | null {
  const [result, setResult] = useState<{
    sessionId: string | null;
    capabilities: ConversationViewCapabilities | null;
  }>(() => ({ sessionId, capabilities: sessionId ? null : {
    browserFilePinsSupported: false,
    pinnedFileEditingSupported: false,
  } }));
  const backendReconnectKey = useBackendReconnectKey();
  const capabilities = result.sessionId === sessionId ? result.capabilities : null;

  useEffect(() => {
    const controller = new AbortController();
    if (!sessionId) {
      setResult({
        sessionId: null,
        capabilities: {
          browserFilePinsSupported: false,
          pinnedFileEditingSupported: false,
        },
      });
      return () => controller.abort();
    }

    setResult({ sessionId, capabilities: null });
    if (bootstrapCapabilities) {
      setResult({ sessionId, capabilities: {
        browserFilePinsSupported: bootstrapCapabilities.browser_file_pins >= 2,
        pinnedFileEditingSupported: bootstrapCapabilities.pinned_file_editing === 1,
      } });
      return () => controller.abort();
    }
    if (bootstrapPending) return () => controller.abort();
    void fetchConversationViewCapabilities(sessionId, getToken, controller.signal)
      .then((nextCapabilities) => {
        if (!controller.signal.aborted) {
          setResult((current) => (
            current.sessionId === sessionId
              ? { sessionId, capabilities: nextCapabilities }
              : current
          ));
        }
      });
    return () => controller.abort();
  }, [backendReconnectKey, getToken, sessionId, bootstrapCapabilities, bootstrapPending]);

  return capabilities;
}
